import { FC, useMemo } from "react";

import { useTime } from "@/app/application/TimeContext";
import {
	getCardBorderStyle,
	getSurfaceBorderWidth,
} from "@/app/application/theme";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { useTaskPage } from "@/features/task/hooks";
import { Task, TaskGroup } from "@/features/task/model/Task/TaskRequest";
import { useSelectedTaskGroups } from "@/storage/useSelectedTaskGroups";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import DonutSmallRoundedIcon from "@mui/icons-material/DonutSmallRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useQueries } from "@tanstack/react-query";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { useFetchTaskGroups, useFetchTasks } from "@/service/TaskQueries";
import { Translate } from "@/app/application/Translate";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const PAGE_PADDING = { xs: 2, sm: 2.5 };
const BOARD_MAX_WIDTH = 1400;

interface TaskDashboardProps {
	searchTerm: string;
	onOpenTab: (tab: "active" | "completed" | "recurring") => void;
}

type TaskStatus = "Active" | "Archived" | "Recurring";
type TaskWithDashboardStatus = Task & { status: TaskStatus };

interface TaskListResponse {
	Items: Task[];
	Total: number;
}

interface TaskGroupsResponse {
	Groups: TaskGroup[];
}

type TaskGroupsPayload = TaskGroupsResponse | TaskGroup[];

const isTaskGroupsResponse = (
	payload: TaskGroupsPayload | undefined,
): payload is TaskGroupsResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		!Array.isArray(payload) &&
		Array.isArray(payload.Groups),
	);

const subtleSurfaceSx = (theme: Theme, color: string) => ({
	bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.14 : 0.08),
	color,
});

const panelSx = (theme: Theme) => ({
	borderRadius: { xs: 2, sm: 3 },
	border: getCardBorderStyle(theme),
	backgroundColor: theme.palette.background.paper,
	paddingBottom: 2,
	overflow: "hidden",
	boxShadow:
		theme.palette.mode === "dark"
			? "0 1px 0 0 rgba(255,255,255,0.04)"
			: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
});

const elevatedInsetSx = (theme: Theme) => ({
	bgcolor:
		theme.palette.mode === "dark"
			? alpha(theme.palette.common.white, 0.03)
			: alpha(theme.palette.common.white, 0.8),
	border: `${getSurfaceBorderWidth(theme)} solid ${alpha(
		theme.palette.divider,
		theme.palette.mode === "dark" ? 0.3 : 0.12,
	)}`,
});

const scrollSx = (theme: Theme) => ({
	"&::-webkit-scrollbar": { width: 6 },
	"&::-webkit-scrollbar-track": { background: "transparent" },
	"&::-webkit-scrollbar-thumb": {
		borderRadius: 999,
		background:
			theme.palette.mode === "dark"
				? "rgba(255,255,255,0.18)"
				: "rgba(15,23,42,0.16)",
	},
});

const statTileSx = (theme: Theme) => ({
	borderRadius: 2,
	border: `${getSurfaceBorderWidth(theme)} solid ${alpha(theme.palette.divider, 0.75)}`,
	backgroundColor:
		theme.palette.mode === "dark"
			? alpha(theme.palette.common.white, 0.015)
			: alpha(theme.palette.primary.main, 0.02),
	px: 1.25,
	py: 1,
});

const cardScrollSx = {
	overflow: "auto",
	pr: 0.5,
};

const statusMeta: Record<
	TaskStatus,
	{
		label: string;
		tab: "active" | "completed" | "recurring";
		icon: typeof PendingActionsOutlinedIcon;
	}
> = {
	Active: {
		label: "Active",
		tab: "active",
		icon: PendingActionsOutlinedIcon,
	},
	Archived: {
		label: "Completed",
		tab: "completed",
		icon: AssignmentTurnedInOutlinedIcon,
	},
	Recurring: {
		label: "Recurring",
		tab: "recurring",
		icon: AutorenewOutlinedIcon,
	},
};

export const TaskDashboard: FC<TaskDashboardProps> = ({
	searchTerm,
	onOpenTab,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const { timezone } = useTime();
	const taskPage = useTaskPage();
	const persistedTaskGroups = useSelectedTaskGroups();
	const selectedGroupIds = taskPage?.selectedGroupIds ?? persistedTaskGroups[0];
	const taskGroupsQuery = useFetchTaskGroups() as unknown as {
		data?: TaskGroupsPayload;
	};
	const taskGroups = taskGroupsQuery.data;
	const availableTaskGroups = useMemo(() => {
		if (Array.isArray(taskGroups)) {
			return taskGroups;
		}
		if (isTaskGroupsResponse(taskGroups)) {
			return taskGroups.Groups;
		}
		return [];
	}, [taskGroups]);
	const availableGroupIdSet = useMemo(
		() => new Set(availableTaskGroups.map((group) => group.Id)),
		[availableTaskGroups],
	);
	const effectiveSelectedGroupIds = useMemo(
		() =>
			[...new Set(selectedGroupIds)].filter((groupId) =>
				availableGroupIdSet.has(groupId),
			),
		[selectedGroupIds, availableGroupIdSet],
	);

	const canQuery = effectiveSelectedGroupIds.length > 0;
	const querySearch = searchTerm.trim();

	const activeTasks = useFetchTasks(
		effectiveSelectedGroupIds,
		"Active",
		1,
		6,
		querySearch,
		undefined,
		canQuery,
	) as unknown as { data?: TaskListResponse };
	const completedTasks = useFetchTasks(
		effectiveSelectedGroupIds,
		"Archived",
		1,
		6,
		querySearch,
		undefined,
		canQuery,
	) as unknown as { data?: TaskListResponse };
	const recurringTasks = useFetchTasks(
		effectiveSelectedGroupIds,
		"Recurring",
		1,
		6,
		querySearch,
		undefined,
		canQuery,
	) as unknown as { data?: TaskListResponse };

	const selectedGroupNames = useMemo(() => {
		return effectiveSelectedGroupIds
			.map(
				(groupId) =>
					availableTaskGroups.find((group) => group.Id === groupId)?.Name,
			)
			.filter((name): name is string => Boolean(name));
	}, [effectiveSelectedGroupIds, availableTaskGroups]);

	const groupStatusQueries = useQueries({
		queries: effectiveSelectedGroupIds.flatMap((groupId) =>
			(["Active", "Archived", "Recurring"] as const).map((status) => ({
				queryKey: ["task-dashboard-group-status", groupId, status, querySearch],
				queryFn: async (): Promise<TaskListResponse> =>
					(await SATOSPortal.GetTasks(
						[groupId],
						status,
						1,
						1,
						querySearch || undefined,
						undefined,
					)) as TaskListResponse,
				enabled: canQuery,
				staleTime: 60_000,
			})),
		),
	});

	const groupStatusRows = useMemo(() => {
		if (!effectiveSelectedGroupIds.length) return [];

		return effectiveSelectedGroupIds
			.map((groupId, index) => {
				const baseIndex = index * 3;
				const active = groupStatusQueries[baseIndex]?.data?.Total ?? 0;
				const completed = groupStatusQueries[baseIndex + 1]?.data?.Total ?? 0;
				const recurring = groupStatusQueries[baseIndex + 2]?.data?.Total ?? 0;
				const total = active + completed + recurring;
				const name =
					availableTaskGroups.find((group) => group.Id === groupId)?.Name ||
					`Group ${groupId}`;

				return {
					groupId,
					name,
					active,
					completed,
					recurring,
					total,
				};
			})
			.sort((left, right) => right.total - left.total);
	}, [groupStatusQueries, effectiveSelectedGroupIds, availableTaskGroups]);

	const maxGroupTotal = useMemo(
		() => Math.max(...groupStatusRows.map((row) => row.total), 1),
		[groupStatusRows],
	);

	const summaryCards = [
		{
			status: "Active" as const,
			total: activeTasks.data?.Total ?? 0,
			accent: theme.palette.primary.main,
			description: "Tasks currently in flight",
		},
		{
			status: "Archived" as const,
			total: completedTasks.data?.Total ?? 0,
			accent: theme.palette.success.main,
			description: "Closed work and finished tasks",
		},
		{
			status: "Recurring" as const,
			total: recurringTasks.data?.Total ?? 0,
			accent: theme.palette.warning.main,
			description: "Scheduled repeat work",
		},
	];

	const recentItems = useMemo(() => {
		const merged: TaskWithDashboardStatus[] = [
			...(activeTasks.data?.Items ?? []).map((task) => ({
				...task,
				status: "Active" as TaskStatus,
			})),
			...(completedTasks.data?.Items ?? []).map((task) => ({
				...task,
				status: "Archived" as TaskStatus,
			})),
			...(recurringTasks.data?.Items ?? []).map((task) => ({
				...task,
				status: "Recurring" as TaskStatus,
			})),
		];

		return merged
			.sort((left, right) => {
				const leftDate =
					dayjs(left?.LastChanged).valueOf() ||
					dayjs(left?.Completed).valueOf() ||
					dayjs(left?.Created).valueOf() ||
					dayjs(left?.Deadline).valueOf() ||
					0;
				const rightDate =
					dayjs(right?.LastChanged).valueOf() ||
					dayjs(right?.Completed).valueOf() ||
					dayjs(right?.Created).valueOf() ||
					dayjs(right?.Deadline).valueOf() ||
					0;

				return rightDate - leftDate;
			})
			.slice(0, 8);
	}, [
		activeTasks.data?.Items,
		completedTasks.data?.Items,
		recurringTasks.data?.Items,
	]);

	const totalVisibleTasks =
		(summaryCards[0]?.total ?? 0) +
		(summaryCards[1]?.total ?? 0) +
		(summaryCards[2]?.total ?? 0);

	const heroStatTiles = [
		{ label: "Groups selected", value: effectiveSelectedGroupIds.length },
		{ label: "Groups with tasks", value: groupStatusRows.length },
		{ label: "Visible tasks", value: totalVisibleTasks },
		{ label: "Recent items", value: recentItems.length },
	];

	if (!canQuery) {
		return (
			<Box
				sx={{
					flex: 1,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: { xs: 2, sm: 3 },
				}}
			>
				<Card
					variant="outlined"
					sx={{
						...panelSx(theme),
						maxWidth: 560,
						borderColor: alpha(theme.palette.primary.main, 0.18),
					}}
				>
					<CardContent>
						<Typography variant="h6" fontWeight={700} gutterBottom>
							<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L372C62.ChooseAtLeastOneTaskGroup">Choose at least one task group</Translate>
						</Typography>
						<Typography variant="body2" color="text.secondary">
							<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L375C58.TheDashboardUsesTheSelectedGroups">The dashboard uses the selected groups from the page header. Once
							a group is selected, the overview and global search will populate.</Translate>
						</Typography>
					</CardContent>
				</Card>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				overflowY: "auto",
				overflowX: "hidden",
				px: PAGE_PADDING,
				pt: 1.5,
				pb: { xs: 2.5, sm: 3 },
				display: "flex",
				flexDirection: "column",
				gap: { xs: 1.5, sm: 2 },
				backgroundColor: "background.default",
				...scrollSx(theme),
				WebkitOverflowScrolling: "touch",
			}}
		>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					gap: { xs: 1.5, sm: 2 },
					maxWidth: BOARD_MAX_WIDTH,
					mx: "auto",
				}}
			>
				<Card
					variant="outlined"
					sx={{
						...panelSx(theme),
						borderColor: alpha(theme.palette.primary.main, 0.14),
					}}
				>
					<CardContent
						sx={{
							display: "grid",
							gridTemplateColumns: {
								xs: "1fr",
								lg: "minmax(0, 1.35fr) minmax(320px, 0.65fr)",
							},
							alignItems: "start",
							gap: 2,
							p: { xs: 2, sm: 2.5 },
						}}
					>
						<Box sx={{ minWidth: 0 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 0.75,
									mb: 0.5,
								}}
							>
								<InsightsRoundedIcon
									sx={{ fontSize: 18, color: "primary.main" }}
								/>
								<Typography
									variant="caption"
									sx={{
										fontWeight: 700,
										color: "primary.main",
										letterSpacing: "0.06em",
										textTransform: "uppercase",
									}}
								>
									<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L452C10.TaskDashboard">Task dashboard</Translate>
								</Typography>
							</Box>
							<Typography
								variant="h4"
								component="h1"
								sx={{
									fontWeight: 700,
									letterSpacing: "-0.02em",
									lineHeight: 1.1,
									fontSize: { xs: "1.4rem", sm: "1.8rem" },
								}}
							>
								<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L465C9.TaskDashboard">Task dashboard</Translate>
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mt: 0.75 }}
							>
								<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L472C9.TrackWorkloadCompletionFlowAndRecurring">Track workload, completion flow, and recurring coverage across
								the selected task groups.</Translate>
							</Typography>

							<Box
								sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.5 }}
							>
								<Chip
									size="small"
									label={`${effectiveSelectedGroupIds.length} groups selected`}
									sx={{
										height: 28,
										borderRadius: 999,
										fontWeight: 800,
										...subtleSurfaceSx(theme, theme.palette.primary.main),
									}}
								/>
								{querySearch ? (
									<Chip
										size="small"
										label={`Search: "${querySearch}"`}
										sx={{
											height: 28,
											borderRadius: 999,
											fontWeight: 800,
											...subtleSurfaceSx(
												theme,
												theme.palette.mode === "dark"
													? theme.palette.warning.light
													: theme.palette.warning.dark,
											),
										}}
									/>
								) : null}
								{selectedGroupNames.slice(0, 2).map((groupName) => (
									<Chip
										key={groupName}
										size="small"
										label={groupName}
										sx={{
											height: 28,
											borderRadius: 999,
											fontWeight: 700,
											bgcolor:
												theme.palette.mode === "dark"
													? alpha(theme.palette.common.white, 0.06)
													: alpha(theme.palette.text.primary, 0.06),
										}}
									/>
								))}
							</Box>
						</Box>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
								gap: 1,
							}}
						>
							{heroStatTiles.map((item) => (
								<Box key={item.label} sx={statTileSx(theme)}>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.04em",
										}}
									>
										{item.label}
									</Typography>
									<Typography
										variant="subtitle2"
										sx={{ fontWeight: 800, mt: 0.2, lineHeight: 1.2 }}
									>
										{item.value}
									</Typography>
								</Box>
							))}
						</Box>
					</CardContent>
				</Card>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							md: "repeat(3, minmax(0, 1fr))",
						},
						gap: 2,
					}}
				>
					{summaryCards.map(({ status, total, accent, description }) => {
						const Icon = statusMeta[status].icon;

						return (
							<Card
								key={status}
								variant="outlined"
								sx={{
									...panelSx(theme),
									borderColor: alpha(accent, 0.18),
									height: "100%",
								}}
							>
								<CardContent
									sx={{
										p: { xs: 1.75, sm: 2 },
										height: "100%",
										display: "flex",
										flexDirection: "column",
										"&:last-child": { pb: { xs: 1.75, sm: 2 } },
									}}
								>
									<Box
										sx={{
											display: "flex",
											alignItems: "flex-start",
											justifyContent: "space-between",
											gap: 1.5,
										}}
									>
										<Box>
											<Typography variant="body2" color="text.secondary">
												{statusMeta[status].label}
											</Typography>
											<Typography
												variant="h4"
												fontWeight={800}
												sx={{ mt: 0.5 }}
											>
												{total}
											</Typography>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{
													mt: 0.5,
													whiteSpace: { xs: "normal", sm: "inherit" },
												}}
											>
												{description}
											</Typography>
										</Box>
										<Box
											sx={{
												width: 44,
												height: 44,
												borderRadius: 2.5,
												display: "grid",
												placeItems: "center",
												bgcolor: alpha(accent, 0.1),
												color: accent,
												flexShrink: 0,
											}}
										>
											<Icon />
										</Box>
									</Box>
									<Button
										size="small"
										endIcon={<ArrowForwardIcon fontSize="small" />}
										onClick={() => onOpenTab(statusMeta[status].tab)}
										sx={{ mt: "auto", pt: 1.5, px: 0, textTransform: "none" }}
									>
										Open {statusMeta[status].label}
									</Button>
								</CardContent>
							</Card>
						);
					})}
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							xl: "minmax(0, 1.2fr) minmax(360px, 0.8fr)",
						},
						gap: 2,
						alignItems: "stretch",
					}}
				>
					<Card
						variant="outlined"
						sx={{
							...panelSx(theme),
							borderColor: alpha(theme.palette.primary.main, 0.14),
							height: { xl: 620 },
						}}
					>
						<CardContent
							sx={{
								p: { xs: 2, sm: 2.5 },
								height: "100%",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box
								sx={{
									display: "flex",
									flexDirection: { xs: "column", lg: "row" },
									alignItems: { xs: "flex-start", lg: "center" },
									justifyContent: "space-between",
									gap: 1.5,
									mb: 2.5,
								}}
							>
								<Box>
									<Box
										sx={{
											display: "inline-flex",
											alignItems: "center",
											gap: 1,
											px: 1.25,
											py: 0.75,
											borderRadius: 999,
											...subtleSurfaceSx(theme, theme.palette.primary.main),
											mb: 1,
										}}
									>
										<InsightsRoundedIcon sx={{ fontSize: 18 }} />
										<Typography variant="caption" fontWeight={800}>
											<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L700C58.TaskStatusPerGroup">Task Status Per Group</Translate>
										</Typography>
									</Box>
									<Typography variant="h6" fontWeight={800}>
										<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L704C52.GroupWorkloadDistribution">Group workload distribution</Translate>
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mt: 0.75 }}
									>
										<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L711C11.AModernSnapshotOfHowActive">A modern snapshot of how active, completed, and recurring
										work is distributed across the selected task groups.</Translate>
									</Typography>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{[
										{
											label: "Active",
											color: theme.palette.primary.main,
										},
										{
											label: "Completed",
											color: theme.palette.success.main,
										},
										{
											label: "Recurring",
											color: theme.palette.warning.main,
										},
									].map((item) => (
										<Chip
											key={item.label}
											size="small"
											icon={
												<DonutSmallRoundedIcon
													sx={{ color: `${item.color} !important` }}
												/>
											}
											label={item.label}
											sx={{
												borderRadius: 999,
												bgcolor: alpha(
													item.color,
													theme.palette.mode === "dark" ? 0.16 : 0.1,
												),
												color: item.color,
												fontWeight: 700,
											}}
										/>
									))}
								</Box>
							</Box>

							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: {
										xs: "1fr",
										lg: "minmax(0, 1.45fr) minmax(280px, 0.85fr)",
									},
									gap: { xs: 1.5, sm: 2 },
									minHeight: 0,
									flex: 1,
								}}
							>
								<Box
									sx={{
										display: "grid",
										gap: 1.25,
										maxHeight: { xs: "none", xl: "100%" },
										...cardScrollSx,
									}}
								>
									{groupStatusRows.map((row) => {
										const activeWidth = (row.active / maxGroupTotal) * 100;
										const completedWidth =
											(row.completed / maxGroupTotal) * 100;
										const recurringWidth =
											(row.recurring / maxGroupTotal) * 100;

										return (
											<Box
												key={row.groupId}
												sx={{
													borderRadius: { xs: 2, sm: 3 },
													border: `${getSurfaceBorderWidth(theme)} solid ${alpha(
														theme.palette.divider,
														0.9,
													)}`,
													...elevatedInsetSx(theme),
													p: { xs: 1.25, sm: 1.5 },
												}}
											>
												<Box
													sx={{
														display: "flex",
														alignItems: "center",
														justifyContent: "space-between",
														gap: 1,
														mb: 1,
													}}
												>
													<Box sx={{ minWidth: 0 }}>
														<Typography
															variant="subtitle2"
															fontWeight={800}
															noWrap
														>
															{row.name}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
														>
															{row.total} total tasks
														</Typography>
													</Box>
													<Chip
														size="small"
														label={`${Math.round((row.total / maxGroupTotal) * 100)}%`}
														sx={{
															borderRadius: 999,
															...subtleSurfaceSx(
																theme,
																theme.palette.primary.main,
															),
															fontWeight: 700,
														}}
													/>
												</Box>

												<Box
													sx={{
														height: 12,
														borderRadius: 999,
														overflow: "hidden",
														display: "flex",
														bgcolor: alpha(theme.palette.text.primary, 0.06),
													}}
												>
													<Box
														sx={{
															width: `${activeWidth}%`,
															bgcolor: theme.palette.primary.main,
															minWidth: row.active ? 6 : 0,
														}}
													/>
													<Box
														sx={{
															width: `${completedWidth}%`,
															bgcolor: theme.palette.success.main,
															minWidth: row.completed ? 6 : 0,
														}}
													/>
													<Box
														sx={{
															width: `${recurringWidth}%`,
															bgcolor: theme.palette.warning.main,
															minWidth: row.recurring ? 6 : 0,
														}}
													/>
												</Box>

												<Box
													sx={{
														display: "grid",
														gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
														gap: 1,
														mt: 1.25,
													}}
												>
													{[
														{
															label: "Active",
															value: row.active,
															color: theme.palette.primary.main,
														},
														{
															label: "Completed",
															value: row.completed,
															color: theme.palette.success.main,
														},
														{
															label: "Recurring",
															value: row.recurring,
															color: theme.palette.warning.main,
														},
													].map((item) => (
														<Box key={item.label}>
															<Typography
																variant="caption"
																color="text.secondary"
																sx={{ display: "block" }}
															>
																{item.label}
															</Typography>
															<Typography
																variant="body2"
																fontWeight={800}
																sx={{ color: item.color }}
															>
																{item.value}
															</Typography>
														</Box>
													))}
												</Box>
											</Box>
										);
									})}
								</Box>

								<Box
									sx={{
										borderRadius: { xs: 2, sm: 3 },
										...elevatedInsetSx(theme),
										p: { xs: 1.5, sm: 2 },
										display: "flex",
										flexDirection: "column",
										gap: 1.25,
										maxHeight: { xs: "none", xl: "100%" },
										...cardScrollSx,
									}}
								>
									<Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
										<Box sx={{ minWidth: isMobile ? 360 : "auto" }}>
											<Box
												sx={{
													display: "grid",
													gridTemplateColumns: "minmax(0, 1fr) repeat(4, auto)",
													gap: 1,
													alignItems: "center",
													px: 1,
													pb: 1,
													borderBottom: `${getSurfaceBorderWidth(theme)} solid ${alpha(
														theme.palette.divider,
														0.9,
													)}`,
												}}
											>
												<Typography
													variant="caption"
													color="text.secondary"
													fontWeight={700}
												>
													<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L944C14.Group">Group</Translate>
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													fontWeight={700}
												>
													<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L951C14.A">A</Translate>
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													fontWeight={700}
												>
													<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L958C14.C">C</Translate>
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													fontWeight={700}
												>
													<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L965C14.R">R</Translate>
												</Typography>
												<Typography
													variant="caption"
													color="text.secondary"
													fontWeight={700}
												>
													<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L972C14.Total">Total</Translate>
												</Typography>
											</Box>

											{groupStatusRows.map((row) => (
												<Box
													key={`summary-${row.groupId}`}
													sx={{
														display: "grid",
														gridTemplateColumns:
															"minmax(0, 1fr) repeat(4, auto)",
														gap: 1,
														alignItems: "center",
														px: 1,
														py: 1,
														borderRadius: 2,
														bgcolor: alpha(theme.palette.primary.main, 0.025),
													}}
												>
													<Typography variant="body2" fontWeight={700} noWrap>
														{row.name}
													</Typography>
													<Typography variant="body2" fontWeight={700}>
														{row.active}
													</Typography>
													<Typography variant="body2" fontWeight={700}>
														{row.completed}
													</Typography>
													<Typography variant="body2" fontWeight={700}>
														{row.recurring}
													</Typography>
													<Chip
														size="small"
														label={row.total}
														sx={{
															height: 24,
															fontWeight: 800,
															bgcolor: alpha(
																theme.palette.text.primary,
																theme.palette.mode === "dark" ? 0.12 : 0.06,
															),
															color: theme.palette.text.primary,
														}}
													/>
												</Box>
											))}
										</Box>
									</Box>
								</Box>
							</Box>
						</CardContent>
					</Card>

					<Card
						variant="outlined"
						sx={{ ...panelSx(theme), height: { xl: 620 } }}
					>
						<CardContent
							sx={{
								p: { xs: 1.75, sm: 2 },
								pb: { xs: 1.75, sm: 1.5 },
								height: "100%",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 1,
									mb: 1.5,
								}}
							>
								<Box>
									<Typography variant="h6" fontWeight={700}>
										<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L1049C52.RecentTasks">Recent tasks</Translate>
									</Typography>
									<Typography variant="body2" color="text.secondary">
										<Translate String="Auto.Features.Task.Ui.Components.TaskDashboard.L1052C61.LatestItemsAcrossActiveCompletedAnd">Latest items across active, completed, and recurring views.</Translate>
									</Typography>
								</Box>
								{querySearch && (
									<Chip size="small" label={`Filtered by "${querySearch}"`} />
								)}
							</Box>

							<Box
								sx={{
									flex: 1,
									minHeight: 0,
									...cardScrollSx,
								}}
							>
								{recentItems.length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										No tasks matched the current filters.
									</Typography>
								) : (
									recentItems.map((task, index) => (
										<Box key={`${task.status}-${task.TaskId}`}>
											{index > 0 && (
												<Divider
													sx={{
														my: 1.25,
														display: { xs: "none", sm: "block" },
													}}
												/>
											)}
											<Box
												sx={{
													display: "flex",
													flexDirection: { xs: "column", md: "row" },
													alignItems: { xs: "flex-start", md: "center" },
													justifyContent: "space-between",
													gap: 1.5,
													border: {
														xs: `${getSurfaceBorderWidth(theme)} solid ${alpha(
															theme.palette.divider,
															0.9,
														)}`,
														sm: "none",
													},
													borderRadius: { xs: 2, sm: 0 },
													p: { xs: 1.25, sm: 0 },
													bgcolor: {
														xs: alpha(theme.palette.primary.main, 0.025),
														sm: "transparent",
													},
												}}
											>
												<Box sx={{ minWidth: 0 }}>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
															flexWrap: "wrap",
														}}
													>
														<Typography
															variant="subtitle1"
															fontWeight={700}
															sx={{
																whiteSpace: { xs: "normal", sm: "nowrap" },
															}}
															noWrap={!isMobile}
														>
															{task.Title}
														</Typography>
														<Chip
															size="small"
															label={statusMeta[task.status].label}
															onClick={() =>
																onOpenTab(statusMeta[task.status].tab)
															}
															sx={{
																bgcolor: alpha(
																	task.status === "Archived"
																		? theme.palette.success.main
																		: task.status === "Recurring"
																			? theme.palette.warning.main
																			: theme.palette.primary.main,
																	0.12,
																),
															}}
														/>
													</Box>
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{
															mt: 0.5,
															display: "-webkit-box",
															WebkitLineClamp: 2,
															WebkitBoxOrient: "vertical",
															overflow: "hidden",
														}}
													>
														{task.Description?.replace(
															/<[^>]*>/g,
															" ",
														).trim() || "No description"}
													</Typography>
												</Box>
												<Box sx={{ textAlign: { xs: "left", md: "right" } }}>
													<Typography variant="body2" fontWeight={600}>
														{task.Deadline
															? dayjs
																	.utc(task.Deadline)
																	.tz(timezone)
																	.format("DD/MM/YY HH:mm:ss")
															: "No deadline"}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{task.User?.Name || "Unassigned"}
													</Typography>
												</Box>
											</Box>
										</Box>
									))
								)}
							</Box>
						</CardContent>
					</Card>
				</Box>
			</Box>
		</Box>
	);
};
